/**
 * Cart options app compatibility fix - v3.5
 * ---------------------------------------------------------------------------
 * NOTE FOR FUTURE THEME UPDATES: this file survives theme version updates,
 * but the <script> tag in layout/theme.liquid that loads it does NOT - the
 * vendor's own theme.liquid replaces it. After every theme update, re-add:
 *
 *   <script src="{{ 'cart-options-compat.js' | asset_url }}" defer="defer"></script>
 *
 * near the end of <body>. See also the console warnings this file emits.
 * ---------------------------------------------------------------------------
 * Fast Product Options performs its own AJAX add-to-cart and then redirects
 * with location.href = '/cart', which bypasses this theme's cart drawer.
 *
 * The location.href setter cannot be overridden (browser security boundary),
 * so this does not try. Instead it intercepts FPO's add-to-cart network call,
 * lets the real request complete, opens this theme's drawer, and then steers
 * FPO down its own error path - so the redirect, which sits in its success
 * path, is never reached. The item is already in the cart at that point.
 *
 * BECAUSE FPO'S SUCCESS PATH NEVER RUNS, ANY STATE IT SETS BEFORE THE REQUEST
 * MUST BE CLEANED UP HERE. Three such things are known, all handled in
 * clearSpinners(): the spinner overlay, the disabled/loading classes, and the
 * inline "pointer-events: none" on the add-to-cart button. If a future symptom
 * looks like "the button is stuck in some state", this is the place to look.
 *
 * Layers, politest first:
 *   1. Keep cartDrawerHook pinned on window.fpob_settings even if the app
 *      replaces that object after init.
 *   2. Inject cartDrawerHook into the app's settings JSON in flight.
 *   3. Terminate the add-to-cart call before its redirect (fetch and XHR).
 *
 * BOUNCE_FALLBACK is an opt-in last resort: if layer 3 ever fails to stop the
 * navigation, this bounces back from /cart to the product page and opens the
 * drawer there. Costs a visible flash, so it is off by default.
 * ---------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var DEBUG = true;
  var BOUNCE_FALLBACK = false;

  // How to terminate FPO's add-to-cart call once the item is safely in the cart.
  //
  //   "reject" - reject FPO's promise. Its own catch/finally still runs, so any
  //              internal "busy" guard is cleared and further adds work without
  //              a page refresh. The redirect lives in the success path, so it
  //              is skipped. This is the default.
  //
  //   "hang"   - never settle the promise. Blocks the redirect absolutely, but
  //              leaves FPO mid-flight: its busy guard stays set and no further
  //              add is possible until the page is reloaded.
  //
  // If "reject" ever causes a redirect to /cart again, or surfaces an error
  // message to customers, switch this back to "hang".
  var ON_COMPLETE = "reject";
  var HOOK = "window.themeCore.EventBus.emit('cart:drawer:refresh-and-open');";

  function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(console, ["[fpo-fix]"].concat([].slice.call(arguments)));
  }

  function here() {
    try {
      return new Error().stack || "";
    } catch (e) {
      return "";
    }
  }

  function isCartAdd(url) {
    return !!url && /\/cart\/add(\.js)?(\?|$)/.test(String(url));
  }

  function stackIsFPO(s) {
    return /fpob|fast-product-options|019f47b1/i.test(s || "");
  }

  // A customized add carries line item properties; a plain theme add does not.
  function hasProps(body) {
    try {
      if (!body) return false;
      if (typeof body === "string") {
        return /properties(\[|%5B|"\s*:)/i.test(body);
      }
      if (typeof FormData !== "undefined" && body instanceof FormData) {
        var f = false;
        body.forEach(function (v, k) {
          if (/^properties\[/i.test(k)) f = true;
        });
        return f;
      }
      if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
        var g = false;
        body.forEach(function (v, k) {
          if (/^properties\[/i.test(k)) g = true;
        });
        return g;
      }
    } catch (e) {}
    return false;
  }

  // A theme version update can rename the drawer event. Rather than assume one
  // name works, fire a strategy, verify the drawer actually opened, and
  // escalate if it did not. Verified against Boutique v2.2.3 and v2.3.3.
  var strategies = [
    function () { window.themeCore.EventBus.emit("cart:drawer:refresh-and-open"); },
    function () { window.themeCore.EventBus.emit("cart:refresh-and-open"); },
    function () { window.themeCore.EventBus.emit("cart:drawer:open"); },
    function () { window.themeCore.EventBus.emit("cart:updated"); },
    function () { document.dispatchEvent(new CustomEvent("cart:updated")); }
  ];

  function drawerIsOpen() {
    try {
      var d = document.querySelector(".cart-drawer");
      return !!d && /\b(is-active|is-open)\b/.test(String(d.className));
    } catch (e) {
      return false;
    }
  }

  var lastOpen = 0;
  function openDrawer() {
    var n = Date.now();
    if (n - lastOpen < 300) return;
    lastOpen = n;
    var i = 0;
    function attempt() {
      if (i >= strategies.length) {
        log("WARNING: no drawer strategy succeeded - theme markup may have changed");
        return;
      }
      try {
        strategies[i]();
        log("drawer strategy " + i + " fired");
      } catch (e) {}
      i++;
      // The drawer refreshes asynchronously, so poll for up to 1.2s before
      // escalating. Escalating too eagerly fires stray theme events for no
      // reason, which is what a single short timeout was doing.
      var waited = 0;
      var poll = setInterval(function () {
        waited += 120;
        if (drawerIsOpen()) {
          clearInterval(poll);
          log("drawer is open");
          return;
        }
        if (waited >= 1200) {
          clearInterval(poll);
          attempt();
        }
      }, 120);
    }
    attempt();
  }

  // Everything FPO sets up before its request and tears down afterwards has to
  // be undone here, because its teardown never runs. Confirmed live:
  //   - .fpob-atc-spinner-wrapper overlay appended inside the button
  //   - loading/disabled classes and the disabled attribute
  //   - inline "pointer-events: none" on the button (makes it unclickable)
  // The button's own label is left intact by FPO, so no label restore needed.
  function clearSpinners() {
    try {
      var spin = document.querySelectorAll(".fpob-atc-spinner-wrapper, .fpob-atc-spinner");
      for (var i = 0; i < spin.length; i++) {
        if (spin[i].parentNode) spin[i].parentNode.removeChild(spin[i]);
      }
      var sel = '[class*="fpob"] button, [id*="fpob"] button, [data-js-product-form] button, form[action*="/cart/add"] button';
      var nodes = document.querySelectorAll(sel);
      for (var j = 0; j < nodes.length; j++) {
        nodes[j].removeAttribute("disabled");
        nodes[j].className = String(nodes[j].className).replace(/\b(is-)?(loading|disabled|busy|processing)\b/gi, "");
        // FPO sets inline "pointer-events: none" on the button for the duration
        // of the add and clears it in the continuation this fix cuts. Left in
        // place it makes the button unclickable until a page refresh.
        if (nodes[j].style && nodes[j].style.pointerEvents === "none") {
          nodes[j].style.pointerEvents = "";
        }
      }
      // If FPO surfaces our own rejection message to the customer, hide it.
      // Deliberately narrow: genuine validation errors are left alone.
      var errs = document.querySelectorAll('[class*="fpob"]');
      for (var m = 0; m < errs.length; m++) {
        if (/cart-options-compat/i.test(errs[m].textContent || "")) {
          errs[m].textContent = "";
        }
      }
    } catch (e) {}
  }

  // Runs immediately, then twice more in case FPO re-adds the overlay late.
  function resetButtons() {
    clearSpinners();
    setTimeout(clearSpinners, 300);
    setTimeout(clearSpinners, 900);
  }

  function markBounce() {
    if (!BOUNCE_FALLBACK) return;
    try {
      sessionStorage.setItem("fpoReturn", JSON.stringify({ u: location.href, t: Date.now() }));
    } catch (e) {}
  }

  function pin(o) {
    if (!o || typeof o !== "object") return o;
    try {
      if (!o.cartDrawerHook) o.cartDrawerHook = HOOK;
      if (o.settings && typeof o.settings === "object" && !o.settings.cartDrawerHook) {
        o.settings.cartDrawerHook = HOOK;
      }
    } catch (e) {}
    return o;
  }

  // --- Layer 1: keep cartDrawerHook pinned on the app's settings object -----
  (function () {
    var store = pin(window.fpob_settings || {});
    try {
      Object.defineProperty(window, "fpob_settings", {
        configurable: true,
        enumerable: true,
        get: function () {
          return store;
        },
        set: function (v) {
          log("fpob_settings replaced, re-pinning hook");
          store = pin(v || {});
        }
      });
      log("settings trap installed");
    } catch (e) {
      window.fpob_settings = store;
      log("settings trap failed, plain assignment used", e);
    }
  })();

  // --- Layers 2 and 3: fetch ------------------------------------------------
  if (typeof window.fetch === "function") {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = "";
      try {
        url = typeof input === "string" ? input : (input && input.url) || "";
      } catch (e) {}
      var body = (init && init.body) || null;
      var fromFPO = stackIsFPO(here());

      if (isCartAdd(url) && (fromFPO || hasProps(body))) {
        log("intercepting add-to-cart (fetch)", url, "stack:", fromFPO, "props:", hasProps(body));
        markBounce();
        return origFetch.apply(this, arguments).then(function (res) {
          if (!res.ok) {
            log("add returned", res.status, "- passing through");
            return res;
          }
          openDrawer();
          resetButtons();
          if (ON_COMPLETE === "reject") {
            log("rejecting FPO's promise so its internal state resets");
            return Promise.reject(
              new Error("cart-options-compat: add completed by theme")
            );
          }
          // Never settles, so FPO's redirect is never reached.
          return new Promise(function () {});
        });
      }

      if (/fpob|fast-product-options|productoptions/i.test(url) && !isCartAdd(url)) {
        return origFetch.apply(this, arguments).then(function (res) {
          var ct = "";
          try {
            ct = res.headers.get("content-type") || "";
          } catch (e) {}
          if (ct.indexOf("json") === -1) return res;
          return res
            .clone()
            .json()
            .then(function (d) {
              pin(d);
              if (d && typeof d === "object") {
                Object.keys(d).forEach(function (k) {
                  if (d[k] && typeof d[k] === "object") pin(d[k]);
                });
              }
              log("hook injected into settings response", url);
              return new Response(JSON.stringify(d), {
                status: res.status,
                statusText: res.statusText,
                headers: res.headers
              });
            })
            .catch(function () {
              return res;
            });
        });
      }

      return origFetch.apply(this, arguments);
    };
    log("fetch wrapped");
  }

  // --- Layer 3: XHR --------------------------------------------------------
  (function () {
    if (typeof XMLHttpRequest === "undefined") return;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__fpoUrl = url;
      this.__fpoStack = here();
      this.__fpoSwallow = false;
      // Registered at open() time so these run before any onload the app
      // assigns afterwards; stopImmediatePropagation then blocks its handler.
      var self = this;
      ["readystatechange", "load", "loadend"].forEach(function (ev) {
        try {
          self.addEventListener(
            ev,
            function (e) {
              if (!self.__fpoSwallow || self.readyState !== 4) return;
              if (self.status >= 200 && self.status < 300) {
                openDrawer();
                resetButtons();
              }
              e.stopImmediatePropagation();
              if (ON_COMPLETE === "reject") {
                // Mirror of the fetch rejection: let FPO's error/finally
                // handlers run so its busy guard clears, while its success
                // handler (which holds the redirect) stays suppressed.
                try {
                  self.dispatchEvent(new Event("error"));
                } catch (err2) {}
              }
            },
            true
          );
        } catch (err) {}
      });
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      if (isCartAdd(this.__fpoUrl) && (stackIsFPO(this.__fpoStack) || hasProps(body))) {
        this.__fpoSwallow = true;
        markBounce();
        log("intercepting add-to-cart (XHR)", this.__fpoUrl);
      }
      return origSend.apply(this, arguments);
    };
    log("XHR wrapped");
  })();

  // --- Opt-in last resort: bounce back off /cart ----------------------------
  if (BOUNCE_FALLBACK) {
    try {
      if (/(^|\/)cart\/?$/.test(location.pathname)) {
        var raw = sessionStorage.getItem("fpoReturn");
        if (raw) {
          var mark = JSON.parse(raw);
          sessionStorage.removeItem("fpoReturn");
          if (mark && mark.u && Date.now() - mark.t < 6000) {
            sessionStorage.setItem("fpoOpen", "1");
            location.replace(mark.u);
          }
        }
      } else if (sessionStorage.getItem("fpoOpen")) {
        sessionStorage.removeItem("fpoOpen");
        var tries = 0;
        var iv = setInterval(function () {
          tries++;
          try {
            window.themeCore.EventBus.emit("cart:drawer:refresh-and-open");
            clearInterval(iv);
          } catch (e) {}
          if (tries > 40) clearInterval(iv);
        }, 150);
      }
    } catch (e) {}
  }

  // Startup self-check. If a future theme update moves these hooks, this says
  // so in the console instead of the fix failing silently.
  setTimeout(function () {
    var missing = [];
    if (!(window.themeCore && window.themeCore.EventBus)) missing.push("themeCore.EventBus");
    if (!document.querySelector(".cart-drawer")) missing.push(".cart-drawer");
    if (missing.length) {
      log("WARNING: expected theme hooks missing:", missing.join(", "), "- re-verify this fix against the current theme version");
    }
  }, 2500);

  log("ready");
})();
